import { useState } from "react";
import {
  shopApi,
  LandingPageSectionApi,
  SectionFormValues,
  SectionMargins,
  CourseGroupListContent,
  SimpleWysiwygContent,
  TwoColumnsWysiwygContent,
  ImageContentSectionContent,
  ImagesCardsContent,
  LogosSectionContent,
  SimpleBgImageContent,
  ClassicHeroContent,
  DynamicSectionContent,
  MainPageHeroContent,
  ProductsSectionContent,
  MapSectionContent,
  OpinionsSectionContent,
  ContactLocationsSectionContent,
  ContactFormSectionContent,
  PeopleSectionContent,
  CardsSectionContent,
  MobileStickyCtaContent,
  StaticPdfContent,
  VideoSectionContent,
  VideoContentCardsSectionContent,
  BlogQuoteContent,
} from "api/shop";
import { FormProvider, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { SectionMarginsConfig, marginsSchema, MARGINS_FORM_NAME } from "./SectionForms/SectionMarginsConfig";
import { normalizeSectionMargins } from "./normalizeSectionMargins";
import { useToaster } from "utils";
import { ModalHeader } from "components/templates/Modals/ModalHeader";
import { ModalPadding } from "components/templates/Modals/ModalPadding";
import { PaziaModal } from "components/molecules";
import { SECTION_TYPES, SECTION_TYPE_LABELS, SectionType } from "consts/sectionTypes";
import { CourseGroupListForm, CourseGroupListFormValues } from "./SectionForms/CourseGroupListForm";
import { SimpleWysiwygForm, SimpleWysiwygFormValues } from "./SectionForms/SimpleWysiwygForm";
import { TwoColumnsWysiwygForm, TwoColumnsWysiwygFormValues } from "./SectionForms/TwoColumnsWysiwygForm";
import { ImageContentForm, ImageContentFormValues } from "./SectionForms/ImageContentForm";
import { ImagesCardsForm, ImagesCardsFormValues } from "./SectionForms/ImagesCardsForm";
import { LogosSectionForm, LogosSectionFormValues } from "./SectionForms/LogosSectionForm";
import { SimpleBgImageForm, SimpleBgImageFormValues } from "./SectionForms/SimpleBgImageForm";
import { ClassicHeroForm, ClassicHeroFormValues } from "./SectionForms/ClassicHeroForm";
import { DynamicSectionForm, DynamicSectionFormValues } from "./SectionForms/DynamicSectionForm";
import { MainPageHeroForm, MainPageHeroFormValues } from "./SectionForms/MainPageHeroForm";
import { ProductsSectionForm, ProductsSectionFormValues } from "./SectionForms/ProductsSectionForm";
import { MapSectionForm, MapSectionFormValues } from "./SectionForms/MapSectionForm";
import { OpinionsSectionForm, OpinionsSectionFormValues } from "./SectionForms/OpinionsSectionForm";
import { ContactLocationsForm, ContactLocationsFormValues } from "./SectionForms/ContactLocationsForm";
import { ContactFormSectionForm, ContactFormSectionFormValues } from "./SectionForms/ContactFormSectionForm";
import { PeopleSectionForm, PeopleSectionFormValues } from "./SectionForms/PeopleSectionForm";
import { CardsSectionForm, CardsSectionFormValues } from "./SectionForms/CardsSectionForm";
import { MobileStickyCtaForm, MobileStickyCtaFormValues } from "./SectionForms/MobileStickyCtaForm";
import { StaticPdfForm, StaticPdfFormValues } from "./SectionForms/StaticPdfForm";
import { VideoSectionForm, VideoSectionFormValues } from "./SectionForms/VideoSectionForm";
import { VideoContentCardsForm, VideoContentCardsFormValues } from "./SectionForms/VideoContentCardsForm";
import { BlogQuoteForm, BlogQuoteFormValues } from "./SectionForms/BlogQuoteForm";

interface EditSectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  landingPageId: number;
  section: LandingPageSectionApi | null;
  onSuccess: () => void;
  onUpdate?: (sectionId: number, data: SectionFormValues) => Promise<void>;
}

export const EditSectionModal = ({
  isOpen,
  onClose,
  landingPageId,
  section,
  onSuccess,
  onUpdate,
}: EditSectionModalProps) => {
  return (
    <PaziaModal id="edit-section-modal" onCloseRequest={onClose} isOpen={isOpen} size="full">
      {isOpen && section && (
        <EditSectionModalInner
          onClose={onClose}
          landingPageId={landingPageId}
          section={section}
          onSuccess={onSuccess}
          onUpdate={onUpdate}
        />
      )}
    </PaziaModal>
  );
};
export const EditSectionModalInner = ({
  onClose,
  landingPageId,
  section,
  onSuccess,
  onUpdate,
}: Omit<EditSectionModalProps, "isOpen">) => {
  const { showSuccess, showError } = useToaster();
  const [isLoading, setIsLoading] = useState(false);
  const [sectionName, setSectionName] = useState(section?.name ?? "");
  const [anchorId, setAnchorId] = useState(section?.anchorId ?? "");

  const commonSchema = yup.object({
    [MARGINS_FORM_NAME]: marginsSchema.optional(),
  });

  const commonMethods = useForm({
    resolver: yupResolver(commonSchema) as any,
    defaultValues: {
      [MARGINS_FORM_NAME]: (section?.margins ?? undefined) as SectionMargins | undefined,
    },
  });

  if (!section) return null;
  const typeLabel = SECTION_TYPE_LABELS[section.type as SectionType] ?? section.type;

  const handleSubmit = async (content: CourseGroupListFormValues | SimpleWysiwygFormValues | TwoColumnsWysiwygFormValues | ImageContentFormValues | ImagesCardsFormValues | LogosSectionFormValues | SimpleBgImageFormValues | ClassicHeroFormValues | DynamicSectionFormValues | MainPageHeroFormValues | ProductsSectionFormValues | MapSectionFormValues | OpinionsSectionFormValues | ContactLocationsFormValues | ContactFormSectionFormValues | PeopleSectionFormValues | CardsSectionFormValues | MobileStickyCtaFormValues | StaticPdfFormValues | VideoSectionFormValues | VideoContentCardsFormValues | BlogQuoteFormValues) => {
    setIsLoading(true);
    try {
      const rawMargins = commonMethods.getValues(MARGINS_FORM_NAME) as Partial<SectionMargins> | undefined;
      const margins = normalizeSectionMargins(rawMargins);
      const data: SectionFormValues = {
        type: section.type,
        name: sectionName || undefined,
        anchorId: anchorId || undefined,
        content,
        ...(margins ? { margins } : {}),
        order: section.order,
      };
      if (onUpdate) {
        await onUpdate(section.id, data);
      } else {
        await shopApi.updateSection(landingPageId, section.id, data);
      }
      showSuccess("Zaktualizowano sekcję");
      onClose();
      onSuccess();
    } catch (e) {
      showError("Nie udało się zaktualizować sekcji");
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  const renderForm = () => {
    switch (section.type) {
      case SECTION_TYPES.COURSE_GROUP_LIST:
        return (
          <CourseGroupListForm
            onSubmit={handleSubmit}
            onCancel={onClose}
            defaultValues={section.content as CourseGroupListContent}
            isLoading={isLoading}
          />
        );
      case SECTION_TYPES.SIMPLE_WYSIWYG:
        return (
          <SimpleWysiwygForm
            onSubmit={handleSubmit}
            onCancel={onClose}
            defaultValues={section.content as SimpleWysiwygContent}
            isLoading={isLoading}
          />
        );
      case SECTION_TYPES.TWO_COLUMNS_WYSIWYG:
        return (
          <TwoColumnsWysiwygForm
            onSubmit={handleSubmit}
            onCancel={onClose}
            defaultValues={section.content as TwoColumnsWysiwygContent}
            isLoading={isLoading}
          />
        );
      case SECTION_TYPES.IMAGE_CONTENT:
        return (
          <ImageContentForm
            onSubmit={handleSubmit}
            onCancel={onClose}
            defaultValues={section.content as ImageContentSectionContent}
            isLoading={isLoading}
          />
        );
      case SECTION_TYPES.IMAGES_CARDS:
        return (
          <ImagesCardsForm
            onSubmit={handleSubmit}
            onCancel={onClose}
            defaultValues={section.content as ImagesCardsContent}
            isLoading={isLoading}
          />
        );
      case SECTION_TYPES.LOGOS_SECTION:
        return (
          <LogosSectionForm
            onSubmit={handleSubmit}
            onCancel={onClose}
            defaultValues={section.content as LogosSectionContent}
            isLoading={isLoading}
          />
        );
      case SECTION_TYPES.SIMPLE_BG_IMAGE:
        return (
          <SimpleBgImageForm
            onSubmit={handleSubmit}
            onCancel={onClose}
            defaultValues={section.content as SimpleBgImageContent}
            isLoading={isLoading}
          />
        );
      case SECTION_TYPES.CLASSIC_HERO:
        return (
          <ClassicHeroForm
            onSubmit={handleSubmit}
            onCancel={onClose}
            defaultValues={section.content as ClassicHeroContent}
            isLoading={isLoading}
          />
        );
      case SECTION_TYPES.DYNAMIC_SECTION:
        return (
          <DynamicSectionForm
            onSubmit={handleSubmit}
            onCancel={onClose}
            defaultValues={section.content as DynamicSectionContent}
            isLoading={isLoading}
          />
        );
      case SECTION_TYPES.MAIN_PAGE_HERO:
        return (
          <MainPageHeroForm
            onSubmit={handleSubmit}
            onCancel={onClose}
            defaultValues={section.content as MainPageHeroContent}
            isLoading={isLoading}
          />
        );
      case SECTION_TYPES.PRODUCTS_SECTION:
        return (
          <ProductsSectionForm
            onSubmit={handleSubmit}
            onCancel={onClose}
            defaultValues={section.content as ProductsSectionContent}
            isLoading={isLoading}
          />
        );
      case SECTION_TYPES.MAP_SECTION:
        return (
          <MapSectionForm
            onSubmit={handleSubmit}
            onCancel={onClose}
            defaultValues={section.content as MapSectionContent}
            isLoading={isLoading}
          />
        );
      case SECTION_TYPES.OPINIONS_SECTION:
        return (
          <OpinionsSectionForm
            onSubmit={handleSubmit}
            onCancel={onClose}
            defaultValues={section.content as OpinionsSectionContent}
            isLoading={isLoading}
          />
        );
      case SECTION_TYPES.CONTACT_LOCATIONS:
        return (
          <ContactLocationsForm
            onSubmit={handleSubmit}
            onCancel={onClose}
            defaultValues={section.content as ContactLocationsSectionContent}
            isLoading={isLoading}
          />
        );
      case SECTION_TYPES.CONTACT_FORM:
        return (
          <ContactFormSectionForm
            onSubmit={handleSubmit}
            onCancel={onClose}
            defaultValues={section.content as ContactFormSectionContent}
            isLoading={isLoading}
          />
        );
      case SECTION_TYPES.PEOPLE_SECTION:
        return (
          <PeopleSectionForm
            onSubmit={handleSubmit}
            onCancel={onClose}
            defaultValues={section.content as PeopleSectionContent}
            isLoading={isLoading}
          />
        );
      case SECTION_TYPES.CARDS_SECTION:
        return (
          <CardsSectionForm
            onSubmit={handleSubmit}
            onCancel={onClose}
            defaultValues={section.content as CardsSectionContent}
            isLoading={isLoading}
          />
        );
      case SECTION_TYPES.MOBILE_STICKY_CTA:
        return (
          <MobileStickyCtaForm
            onSubmit={handleSubmit}
            onCancel={onClose}
            defaultValues={section.content as MobileStickyCtaContent}
            isLoading={isLoading}
          />
        );
      case SECTION_TYPES.STATIC_PDF:
        return (
          <StaticPdfForm
            onSubmit={handleSubmit}
            onCancel={onClose}
            defaultValues={section.content as StaticPdfContent}
            isLoading={isLoading}
          />
        );
      case SECTION_TYPES.VIDEO_SECTION:
        return (
          <VideoSectionForm
            onSubmit={handleSubmit}
            onCancel={onClose}
            defaultValues={section.content as VideoSectionContent}
            isLoading={isLoading}
          />
        );
      case SECTION_TYPES.VIDEO_CONTENT_CARDS:
        return (
          <VideoContentCardsForm
            onSubmit={handleSubmit}
            onCancel={onClose}
            defaultValues={section.content as VideoContentCardsSectionContent}
            isLoading={isLoading}
          />
        );
      case SECTION_TYPES.BLOG_QUOTE:
        return (
          <BlogQuoteForm
            onSubmit={handleSubmit}
            onCancel={onClose}
            defaultValues={section.content as BlogQuoteContent}
            isLoading={isLoading}
          />
        );
      default:
        return (
          <div className="text-center text-slate-500 dark:text-slate-400">Nieznany typ sekcji: {section.type}</div>
        );
    }
  };

  return (
    <ModalPadding>
      <ModalHeader>
        Edytuj sekcję <span className="text-slate-500">({typeLabel})</span>
      </ModalHeader>
      <div className="mb-4 max-w-md">
        <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
          Nazwa sekcji
        </label>
        <input
          type="text"
          value={sectionName}
          onChange={(e) => setSectionName(e.target.value)}
          placeholder="np. Sekcja główna"
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500"
        />
      </div>
      <div className="mb-4 max-w-md">
        <label className="mb-1 block text-sm font-medium text-slate-700 dark:text-slate-300">
          Identyfikator (anchor)
        </label>
        <input
          type="text"
          value={anchorId}
          onChange={(e) => setAnchorId(e.target.value)}
          placeholder="np. kursy"
          className="w-full rounded-lg border border-slate-300 px-3 py-2 text-slate-900 placeholder-slate-400 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder-slate-500"
        />
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">Używany w linkach: #kursy</p>
      </div>
      <FormProvider {...commonMethods}>
        <div className="mb-4 max-w-3xl">
          <SectionMarginsConfig />
        </div>
      </FormProvider>
      {renderForm()}
    </ModalPadding>
  );
};
